import type {
  Page,
  StockAdjustment,
  StockAdjustmentInput,
  StockAdjustmentListItem,
  StockAdjustmentSummary,
  StockAdjustmentSummaryQuery,
  StockLevel,
  StockMovement,
  StockTransfer,
  StockTransferInput,
  StockTransferListItem,
  StockTransferSummary,
  StockTransferSummaryQuery,
  StockTransferUpdate,
  Warehouse,
  WarehouseInput,
  WarehouseOption,
} from '@sahab/contracts'
import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from '../../lib/api'
import { invalidateLedger } from '../accounting/queries'
import type { AdjustmentListParams, MovementListParams, StockListParams, TransferListParams } from './search'

export const inventoryKeys = {
  warehouses: ['warehouses'] as const,
  stock: ['stock'] as const,
  stockList: (query: StockListParams) => [...inventoryKeys.stock, 'list', query] as const,
  movements: ['stock-movements'] as const,
  movementList: (query: MovementListParams) => [...inventoryKeys.movements, 'list', query] as const,
  adjustments: ['stock-adjustments'] as const,
  adjustmentList: (query: AdjustmentListParams) => [...inventoryKeys.adjustments, 'list', query] as const,
  adjustmentSummary: (query: StockAdjustmentSummaryQuery) =>
    [...inventoryKeys.adjustments, 'summary', query] as const,
  onHand: (variantId: string, warehouseId: string) =>
    [...inventoryKeys.stock, 'on-hand', variantId, warehouseId] as const,
  adjustment: (id: string) => [...inventoryKeys.adjustments, 'detail', id] as const,
  transfers: ['stock-transfers'] as const,
  transferList: (query: TransferListParams) => [...inventoryKeys.transfers, 'list', query] as const,
  transferSummary: (query: StockTransferSummaryQuery) =>
    [...inventoryKeys.transfers, 'summary', query] as const,
  transfer: (id: string) => [...inventoryKeys.transfers, 'detail', id] as const,
}

/** The catalogue's product queries (`catalogKeys.products`; not imported: catalog imports this file). */
const PRODUCT_KEY = ['products'] as const

const path = (base: string, id: string, action?: string) =>
  `${base}/${encodeURIComponent(id)}${action ? `/${action}` : ''}`

// ── warehouses ───────────────────────────────────────────────────────────────

export const warehousesQueryOptions = queryOptions({
  queryKey: inventoryKeys.warehouses,
  queryFn: ({ signal }) => api.get<Warehouse[]>('/warehouses', { signal }),
  staleTime: 5 * 60_000,
})

/**
 * Active warehouses for document forms (GET /warehouses/options): no stock figures, so any
 * document-entry permission may read them (purchases, supplier returns, tills), not only inventory.read.
 */
export const warehouseOptionsQueryOptions = queryOptions({
  queryKey: [...inventoryKeys.warehouses, 'options'] as const,
  queryFn: ({ signal }) => api.get<WarehouseOption[]>('/warehouses/options', { signal }),
  staleTime: 5 * 60_000,
})

/** Warehouse codes appear on stock movements; the main one is the default on documents. */
function invalidateWarehouseViews(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: inventoryKeys.warehouses }),
    queryClient.invalidateQueries({ queryKey: inventoryKeys.movements }),
  ])
}

export function useSaveWarehouse() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: WarehouseInput & { version?: number } }) =>
      id ? api.put<Warehouse>(path('/warehouses', id), input) : api.post<Warehouse>('/warehouses', input),
    onSuccess: () => invalidateWarehouseViews(queryClient),
  })
}

export function useSetMainWarehouse() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(path('/warehouses', id, 'set-main')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: inventoryKeys.warehouses }),
  })
}

export function useArchiveWarehouse() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(path('/warehouses', id, 'archive')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: inventoryKeys.warehouses }),
  })
}

export function useRestoreWarehouse() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(path('/warehouses', id, 'restore')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: inventoryKeys.warehouses }),
  })
}

// ── stock levels and movements ───────────────────────────────────────────────

export function stockLevelsQueryOptions(query: StockListParams) {
  return queryOptions({
    queryKey: inventoryKeys.stockList(query),
    queryFn: ({ signal }) =>
      api.get<Page<StockLevel>>('/stock', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          q: query.q,
          warehouseId: query.warehouseId,
          belowReorder: query.belowReorder,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

/**
 * What one variant has on hand in one warehouse now (a count sheet's system quantity): the stock
 * level's quantity, or zero when the warehouse has never held it.
 */
export function variantOnHandQueryOptions(variantId: string, warehouseId: string) {
  return queryOptions({
    queryKey: inventoryKeys.onHand(variantId, warehouseId),
    queryFn: async ({ signal }) => {
      const result = await api.get<Page<StockLevel>>('/stock', {
        signal,
        query: { variantId, warehouseId, page: 1, pageSize: 1 },
      })
      return result.items.find((row) => row.variantId === variantId)?.quantity ?? '0'
    },
    staleTime: 30_000,
  })
}

export function stockMovementsQueryOptions(query: MovementListParams) {
  return queryOptions({
    queryKey: inventoryKeys.movementList(query),
    queryFn: ({ signal }) =>
      api.get<Page<StockMovement>>('/stock-movements', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          variantId: query.variantId,
          productId: query.productId,
          warehouseId: query.warehouseId,
          reason: query.reason,
          from: query.from,
          to: query.to,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

// ── stock adjustments ────────────────────────────────────────────────────────

export function stockAdjustmentListQueryOptions(query: AdjustmentListParams) {
  return queryOptions({
    queryKey: inventoryKeys.adjustmentList(query),
    queryFn: ({ signal }) =>
      api.get<Page<StockAdjustmentListItem>>('/stock-adjustments', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          q: query.q,
          kind: query.kind,
          reason: query.reason,
          status: query.status,
          warehouseId: query.warehouseId,
          from: query.from,
          to: query.to,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

/** The adjustments list's filters that its figures strip follows (not the view, paging or sort). */
export function toAdjustmentSummaryQuery(query: AdjustmentListParams): StockAdjustmentSummaryQuery {
  return {
    q: query.q,
    reason: query.reason,
    warehouseId: query.warehouseId,
    from: query.from,
    to: query.to,
  }
}

/** Counts per view and six months of write-offs and gains (GET /stock-adjustments/summary). */
export function stockAdjustmentSummaryQueryOptions(query: StockAdjustmentSummaryQuery) {
  return queryOptions({
    queryKey: inventoryKeys.adjustmentSummary(query),
    queryFn: ({ signal }) =>
      api.get<StockAdjustmentSummary>('/stock-adjustments/summary', {
        signal,
        query: {
          q: query.q,
          reason: query.reason,
          warehouseId: query.warehouseId,
          from: query.from,
          to: query.to,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

export function stockAdjustmentQueryOptions(id: string) {
  return queryOptions({
    queryKey: inventoryKeys.adjustment(id),
    queryFn: ({ signal }) => api.get<StockAdjustment>(path('/stock-adjustments', id), { signal }),
  })
}

/** Stock changes move quantities, values and the ledger (inventory against its counter-account). */
function useAdjustmentMutation<TInput>(request: (input: TInput) => Promise<StockAdjustment>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: (adjustment) => {
      queryClient.setQueryData(inventoryKeys.adjustment(adjustment.id), adjustment)
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: inventoryKeys.stock }),
        queryClient.invalidateQueries({ queryKey: inventoryKeys.movements }),
        queryClient.invalidateQueries({ queryKey: [...inventoryKeys.adjustments, 'list'] }),
        queryClient.invalidateQueries({ queryKey: [...inventoryKeys.adjustments, 'summary'] }),
        // On hand on the product list and the product 360.
        queryClient.invalidateQueries({ queryKey: PRODUCT_KEY }),
        invalidateLedger(queryClient),
      ])
    },
  })
}

export function useCreateStockAdjustment() {
  return useAdjustmentMutation((input: StockAdjustmentInput) =>
    api.post<StockAdjustment>('/stock-adjustments', input),
  )
}

export function useVoidStockAdjustment(id: string) {
  return useAdjustmentMutation((reason: string) =>
    api.post<StockAdjustment>(path('/stock-adjustments', id, 'void'), { reason }),
  )
}

// ── stock transfers ──────────────────────────────────────────────────────────

/** The transfers list's filters that its figures strip follows (not the status, paging or sort). */
export function toTransferSummaryQuery(query: TransferListParams): StockTransferSummaryQuery {
  return { q: query.q, warehouseId: query.warehouseId, from: query.from, to: query.to }
}

/** Counts per status and six months of posted transfers (GET /stock-transfers/summary). */
export function stockTransferSummaryQueryOptions(query: StockTransferSummaryQuery) {
  return queryOptions({
    queryKey: inventoryKeys.transferSummary(query),
    queryFn: ({ signal }) =>
      api.get<StockTransferSummary>('/stock-transfers/summary', {
        signal,
        query: { q: query.q, warehouseId: query.warehouseId, from: query.from, to: query.to },
      }),
    placeholderData: keepPreviousData,
  })
}

export function stockTransferListQueryOptions(query: TransferListParams) {
  return queryOptions({
    queryKey: inventoryKeys.transferList(query),
    queryFn: ({ signal }) =>
      api.get<Page<StockTransferListItem>>('/stock-transfers', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          q: query.q,
          status: query.status,
          warehouseId: query.warehouseId,
          from: query.from,
          to: query.to,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

export function stockTransferQueryOptions(id: string) {
  return queryOptions({
    queryKey: inventoryKeys.transfer(id),
    queryFn: ({ signal }) => api.get<StockTransfer>(path('/stock-transfers', id), { signal }),
  })
}

/**
 * A transfer moves quantities between warehouses (never the value of stock, so not the ledger):
 * posting and voiding refresh stock levels, movements and the transfer lists.
 */
function useTransferMutation<TInput>(
  request: (input: TInput) => Promise<StockTransfer>,
  options: { stock?: boolean } = {},
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: (transfer) => {
      queryClient.setQueryData(inventoryKeys.transfer(transfer.id), transfer)
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: inventoryKeys.transfers }),
        options.stock ? queryClient.invalidateQueries({ queryKey: inventoryKeys.stock }) : undefined,
        options.stock ? queryClient.invalidateQueries({ queryKey: inventoryKeys.movements }) : undefined,
        options.stock ? queryClient.invalidateQueries({ queryKey: PRODUCT_KEY }) : undefined,
      ])
    },
  })
}

export function useSaveStockTransfer(id: string | null) {
  return useTransferMutation((input: StockTransferInput | StockTransferUpdate) =>
    id
      ? api.put<StockTransfer>(path('/stock-transfers', id), input)
      : api.post<StockTransfer>('/stock-transfers', input),
  )
}

export function usePostStockTransfer(id: string) {
  return useTransferMutation(
    (version: number) => api.post<StockTransfer>(path('/stock-transfers', id, 'post'), { version }),
    { stock: true },
  )
}

export function useVoidStockTransfer(id: string) {
  return useTransferMutation(
    (reason: string) => api.post<StockTransfer>(path('/stock-transfers', id, 'void'), { reason }),
    { stock: true },
  )
}

export function useDeleteStockTransfer(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<void>(path('/stock-transfers', id)),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: inventoryKeys.transfer(id) })
      return queryClient.invalidateQueries({ queryKey: inventoryKeys.transfers })
    },
  })
}
